<template>
  <HrPageLayout
    title="Compliance Dashboard"
    subtitle="Statistics, filters and employee compliance by department"
    icon="📊"
    :breadcrumb-items="[
      { label: 'Home', route: '/dashboard' },
      { label: 'Compliance Module' },
      { label: 'Compliance Dashboard' }
    ]"
  >
    <template #header-actions>
      <button type="button" class="hr-btn hr-btn-outline-primary" :disabled="loading" @click="loadDashboard">
        <i class="bx bx-refresh" :class="{ 'bx-spin': loading }"></i> Refresh
      </button>
      <div class="cd-export-group">
        <button type="button" class="hr-btn hr-btn-outline-success" :disabled="exporting || !employees.length" @click="exportCsv">
          <i class="bx bx-download"></i> CSV
        </button>
        <button type="button" class="hr-btn hr-btn-outline-success" :disabled="exporting" @click="exportPdf">
          <span v-if="exporting" class="hr-spinner small"></span>
          <i v-else class="bx bx-file"></i> PDF
        </button>
      </div>
      <button type="button" class="hr-btn hr-btn-primary" @click="goEmployeesCompliance">
        <i class="mdi mdi-shield-alert"></i> Employees Compliance
      </button>
    </template>

    <div class="cd-wrap">
      <div class="hr-chart-card hr-mb-4">
        <div class="hr-chart-header">
          <h5 class="hr-chart-title"><i class="bx bx-filter-alt"></i> Filters</h5>
        </div>
        <div class="hr-chart-body">
          <div class="cd-filters">
            <label class="cd-field cd-field-wide">
              <span>Search employee</span>
              <input
                v-model="filters.search"
                type="search"
                class="hr-input"
                placeholder="Name, employee number or ID number"
                @input="debouncedLoad"
              >
            </label>
            <label class="cd-field">
              <span>Year</span>
              <select v-model.number="filters.year" class="hr-select" @change="loadDashboard">
                <option v-for="y in yearOptions" :key="'y-' + y" :value="y">{{ y }}</option>
              </select>
            </label>
            <label class="cd-field">
              <span>Department</span>
              <select v-model="filters.departmentName" class="hr-select" @change="loadDashboard">
                <option value="">All departments</option>
                <option v-for="d in departments" :key="'d-' + d" :value="d">{{ d }}</option>
              </select>
            </label>
            <label class="cd-field">
              <span>Status</span>
              <select v-model="filters.complianceStatus" class="hr-select" @change="loadDashboard">
                <option value="all">All (Compliant &amp; Non-compliant)</option>
                <option value="compliant">Compliant only</option>
                <option value="non_compliant">Non-compliant only</option>
              </select>
            </label>
            <label class="cd-field">
              <span>Compliance area</span>
              <select v-model="filters.area" class="hr-select" @change="loadDashboard">
                <option value="skills">Skills Audit</option>
                <option value="wealth">Wealth Declaration</option>
                <option value="confirmation">Confirmation Due</option>
              </select>
            </label>
            <div class="cd-field cd-field-actions">
              <button type="button" class="hr-btn hr-btn-outline-secondary" @click="resetFilters">Clear filters</button>
            </div>
          </div>
        </div>
      </div>

      <div v-if="loadError" class="hr-alert hr-alert-danger hr-mb-3">
        {{ loadError }}
        <button type="button" class="hr-btn hr-btn-sm hr-mt-2" @click="loadDashboard">Retry</button>
      </div>

      <div v-if="loading && !stats.totalEmployees && !loadError" class="hr-loading-wrap">
        <div class="hr-spinner"></div>
        <p class="hr-text-muted">Loading compliance statistics…</p>
      </div>

      <template v-else>
        <div class="hr-metrics-grid hr-mb-4">
          <div class="hr-metric-card gradient-1">
            <div class="hr-metric-icon"><i class="bx bx-group"></i></div>
            <p class="hr-metric-label">Total employees</p>
            <h2 class="hr-metric-value">{{ stats.totalEmployees || 0 }}</h2>
            <p class="hr-small hr-text-muted">In current department/search filter</p>
          </div>
          <div class="hr-metric-card gradient-2">
            <div class="hr-metric-icon"><i class="bx bx-check-circle"></i></div>
            <p class="hr-metric-label">List compliant</p>
            <h2 class="hr-metric-value">{{ stats.listCompliant || 0 }}</h2>
            <p class="hr-small hr-text-muted">{{ areaLabel }}</p>
          </div>
          <div class="hr-metric-card gradient-3">
            <div class="hr-metric-icon"><i class="bx bx-x-circle"></i></div>
            <p class="hr-metric-label">List non-compliant</p>
            <h2 class="hr-metric-value">{{ stats.listNonCompliant || 0 }}</h2>
            <p class="hr-small hr-text-muted">{{ areaLabel }}</p>
          </div>
          <div class="hr-metric-card gradient-4">
            <div class="hr-metric-icon"><i class="bx bx-book-open"></i></div>
            <p class="hr-metric-label">Skills rate</p>
            <h2 class="hr-metric-value">{{ stats.skillsRate || 0 }}%</h2>
            <p class="hr-small hr-text-muted">{{ stats.skillsCompliant || 0 }} / {{ stats.skillsNonCompliant || 0 }}</p>
          </div>
          <div class="hr-metric-card gradient-1">
            <div class="hr-metric-icon"><i class="bx bx-wallet"></i></div>
            <p class="hr-metric-label">Wealth rate</p>
            <h2 class="hr-metric-value">{{ stats.wealthRate || 0 }}%</h2>
            <p class="hr-small hr-text-muted">{{ stats.wealthCompliant || 0 }} / {{ stats.wealthNonCompliant || 0 }}</p>
          </div>
          <div class="hr-metric-card gradient-2">
            <div class="hr-metric-icon"><i class="bx bx-user-check"></i></div>
            <p class="hr-metric-label">Confirmation due</p>
            <h2 class="hr-metric-value">{{ stats.confirmationDue || 0 }}</h2>
            <p class="hr-small hr-text-muted">{{ stats.onProbation || 0 }} on probation</p>
          </div>
        </div>

        <div class="cd-charts">
          <div class="hr-chart-card">
            <div class="hr-chart-header">
              <h5 class="hr-chart-title"><i class="bx bx-bar-chart-alt-2"></i> Compliance by area ({{ filters.year }})</h5>
            </div>
            <div class="hr-chart-body">
              <div class="cd-canvas-wrap"><canvas ref="areasChartCanvas"></canvas></div>
            </div>
          </div>
          <div class="hr-chart-card">
            <div class="hr-chart-header">
              <h5 class="hr-chart-title"><i class="bx bx-buildings"></i> Compliant vs non-compliant by department</h5>
            </div>
            <div class="hr-chart-body">
              <div class="cd-canvas-wrap cd-canvas-tall"><canvas ref="deptChartCanvas"></canvas></div>
              <p v-if="!byDepartment.length" class="hr-text-muted hr-text-center hr-mt-2">No department data for this filter.</p>
            </div>
          </div>
        </div>

        <div class="hr-chart-card hr-mt-4">
          <div class="hr-chart-header hr-flex-between">
            <h5 class="hr-chart-title">
              <i class="bx bx-list-ul"></i>
              Employees — {{ areaLabel }}
              <span class="hr-badge hr-badge-secondary hr-ms-2">{{ employees.length }}</span>
            </h5>
          </div>
          <div class="hr-chart-body">
            <div class="hr-table-wrapper">
              <table class="hr-table hr-table-hover">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Employee</th>
                    <th>Emp No</th>
                    <th>ID No</th>
                    <th>Department</th>
                    <th>Position</th>
                    <th>Status</th>
                    <th v-if="filters.area === 'confirmation'">Probation end</th>
                    <th v-if="filters.area === 'confirmation'">Days left</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(r, idx) in employees" :key="'e-' + r.employee_id + '-' + idx">
                    <td>{{ idx + 1 }}</td>
                    <td><strong>{{ r.employee_name || '—' }}</strong></td>
                    <td>{{ r.empNo || '—' }}</td>
                    <td>{{ r.nationalId || '—' }}</td>
                    <td>{{ r.departmentName || '—' }}</td>
                    <td>{{ r.positionTitle || '—' }}</td>
                    <td>
                      <span
                        class="hr-badge"
                        :class="r.compliance_status === 'compliant' ? 'hr-badge-success' : 'hr-badge-danger'"
                      >
                        {{ r.compliance_status === 'compliant' ? 'Compliant' : 'Non-compliant' }}
                      </span>
                    </td>
                    <td v-if="filters.area === 'confirmation'">{{ r.probation_end || '—' }}</td>
                    <td v-if="filters.area === 'confirmation'">{{ r.remaining_days != null ? r.remaining_days : '—' }}</td>
                  </tr>
                  <tr v-if="!employees.length">
                    <td :colspan="filters.area === 'confirmation' ? 9 : 7" class="hr-text-center hr-text-muted hr-py-4">
                      No employees match the selected filters.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div class="hr-chart-card hr-mt-4">
          <div class="hr-chart-header">
            <h5 class="hr-chart-title"><i class="bx bx-table"></i> Department breakdown ({{ areaLabel }})</h5>
          </div>
          <div class="hr-chart-body">
            <div class="hr-table-wrapper">
              <table class="hr-table hr-table-hover">
                <thead>
                  <tr>
                    <th>Department</th>
                    <th>Compliant</th>
                    <th>Non-compliant</th>
                    <th>Total</th>
                    <th>Rate</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="d in byDepartment" :key="'dept-' + d.department">
                    <td><strong>{{ d.department }}</strong></td>
                    <td><span class="hr-badge hr-badge-success">{{ d.compliant || 0 }}</span></td>
                    <td><span class="hr-badge hr-badge-danger">{{ d.non_compliant || 0 }}</span></td>
                    <td>{{ d.total || 0 }}</td>
                    <td>{{ deptRate(d) }}%</td>
                  </tr>
                  <tr v-if="!byDepartment.length">
                    <td colspan="5" class="hr-text-center hr-text-muted">No department totals.</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <p v-if="generatedAt" class="hr-text-muted hr-small hr-mt-3">Last updated: {{ generatedAt }} · Year {{ filters.year }}</p>
      </template>
    </div>
  </HrPageLayout>
</template>

<script>
/* eslint-disable */
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { authUrl, execute } from "@/api";
import Chart from "chart.js/auto";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { addCountyLetterhead, loadCountyLogoDataUrl } from "@/utils/payrollReconPdf";

export default {
  name: "ComplianceDashboard",
  components: { HrPageLayout },
  data() {
    const y = new Date().getFullYear();
    return {
      loading: false,
      exporting: false,
      loadError: "",
      yearOptions: [y, y - 1, y - 2, y - 3],
      departments: [],
      letterhead: null,
      debounceTimer: null,
      filters: {
        year: y,
        departmentName: "",
        complianceStatus: "all",
        search: "",
        area: "skills"
      },
      stats: {},
      areas: [],
      byDepartment: [],
      employees: [],
      generatedAt: "",
      charts: { areas: null, dept: null }
    };
  },
  computed: {
    areaLabel() {
      if (this.filters.area === "wealth") return "Wealth Declaration";
      if (this.filters.area === "confirmation") return "Confirmation Due";
      return "Skills Audit";
    }
  },
  mounted() {
    this.loadDepartments();
    this.loadLetterhead();
    this.loadDashboard();
  },
  beforeUnmount() {
    this.destroyCharts();
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
  },
  methods: {
    goEmployeesCompliance() {
      this.$router.push({ name: "module-compliance-unit" });
    },
    debouncedLoad() {
      if (this.debounceTimer) clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => this.loadDashboard(), 350);
    },
    resetFilters() {
      this.filters = {
        year: new Date().getFullYear(),
        departmentName: "",
        complianceStatus: "all",
        search: "",
        area: "skills"
      };
      this.loadDashboard();
    },
    deptRate(d) {
      const t = Number(d.total || 0);
      if (!t) return 0;
      return Math.round((Number(d.compliant || 0) / t) * 1000) / 10;
    },
    destroyCharts() {
      Object.keys(this.charts).forEach((k) => {
        if (this.charts[k]) {
          this.charts[k].destroy();
          this.charts[k] = null;
        }
      });
    },
    async loadLetterhead() {
      try {
        const data = new FormData();
        data.append("function", "getCompanyInfo");
        const res = await execute(data, authUrl);
        if (res?.data?.success && res.data.data?.companyInfo) {
          const c = res.data.data.companyInfo;
          const abs = (u) => {
            if (!u) return "";
            if (/^https?:\/\//i.test(u)) return u;
            try {
              return new URL(u, authUrl).href;
            } catch (_) {
              return u;
            }
          };
          this.letterhead = {
            ...c,
            logoPrimaryUrl: abs(c.logoPrimaryUrl || c.logoPrimary),
            logoSecondaryUrl: abs(c.logoSecondaryUrl || c.logoSecondary)
          };
        }
      } catch (_) {
        this.letterhead = null;
      }
    },
    async loadDepartments() {
      try {
        const fd = new FormData();
        fd.append("function", "getDepartments");
        const res = await execute(fd, authUrl);
        const payload = res?.data?.data || res?.data || {};
        let list = payload.departments || payload.Records || payload || [];
        if (!Array.isArray(list)) list = [];
        this.departments = list
          .map((d) => d.departmentName || d.name || "")
          .filter(Boolean)
          .sort((a, b) => a.localeCompare(b));
      } catch (e) {
        this.departments = [];
      }
    },
    async loadDashboard() {
      this.loading = true;
      this.loadError = "";
      try {
        const fd = new FormData();
        fd.append("function", "getComplianceDashboard");
        fd.append("year", String(this.filters.year));
        fd.append("complianceStatus", this.filters.complianceStatus || "all");
        fd.append("area", this.filters.area || "skills");
        if (this.filters.departmentName) fd.append("departmentName", this.filters.departmentName);
        if (this.filters.search) fd.append("search", this.filters.search.trim());
        const res = await execute(fd, authUrl);
        const body = res?.data || {};
        if (body.success === false) {
          this.loadError = body.message || "Failed to load Compliance Dashboard.";
          return;
        }
        const payload = body.data || {};
        this.stats = payload.stats || {};
        this.areas = payload.areas || [];
        this.byDepartment = payload.byDepartment || [];
        this.employees = payload.employees || [];
        this.generatedAt = payload.generatedAt || "";
        this.$nextTick(() => this.renderCharts());
      } catch (e) {
        this.loadError = e?.message || "Failed to load Compliance Dashboard.";
      } finally {
        this.loading = false;
      }
    },
    renderCharts() {
      this.destroyCharts();
      this.renderAreasChart();
      this.renderDeptChart();
    },
    renderAreasChart() {
      const el = this.$refs.areasChartCanvas;
      if (!el || !this.areas.length) return;
      this.charts.areas = new Chart(el, {
        type: "bar",
        data: {
          labels: this.areas.map((a) => a.label),
          datasets: [
            {
              label: "Compliant",
              data: this.areas.map((a) => Number(a.compliant || 0)),
              backgroundColor: "#198754"
            },
            {
              label: "Non-compliant",
              data: this.areas.map((a) => Number(a.non_compliant || 0)),
              backgroundColor: "#dc3545"
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { position: "bottom" } },
          scales: { y: { beginAtZero: true, ticks: { precision: 0 } } }
        }
      });
    },
    renderDeptChart() {
      const el = this.$refs.deptChartCanvas;
      if (!el || !this.byDepartment.length) return;
      const labels = this.byDepartment.map((r) => r.department);
      this.charts.dept = new Chart(el, {
        type: "bar",
        data: {
          labels,
          datasets: [
            {
              label: "Compliant",
              data: this.byDepartment.map((r) => Number(r.compliant || 0)),
              backgroundColor: "#198754"
            },
            {
              label: "Non-compliant",
              data: this.byDepartment.map((r) => Number(r.non_compliant || r.count || 0)),
              backgroundColor: "#dc3545"
            }
          ]
        },
        options: {
          indexAxis: "y",
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { position: "bottom" } },
          scales: { x: { beginAtZero: true, stacked: false, ticks: { precision: 0 } } }
        }
      });
    },
    exportCsv() {
      const isConf = this.filters.area === "confirmation";
      const headers = isConf
        ? ["Employee", "Emp No", "ID No", "Department", "Position", "Status", "Probation End", "Days Left"]
        : ["Employee", "Emp No", "ID No", "Department", "Position", "Status"];
      const body = this.employees.map((r) => {
        const base = [
          r.employee_name || "",
          r.empNo || "",
          r.nationalId || "",
          r.departmentName || "",
          r.positionTitle || "",
          r.compliance_status === "compliant" ? "Compliant" : "Non-compliant"
        ];
        if (isConf) base.push(r.probation_end || "", r.remaining_days ?? "");
        return base;
      });
      const csv = [headers, ...body]
        .map((row) => row.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","))
        .join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `compliance_dashboard_${this.filters.area}_${this.filters.year}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
    async exportPdf() {
      this.exporting = true;
      try {
        if (!this.letterhead) await this.loadLetterhead();
        const logoDataUrl = await loadCountyLogoDataUrl(this.letterhead);
        const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
        const statusLabel =
          this.filters.complianceStatus === "compliant"
            ? "Compliant only"
            : this.filters.complianceStatus === "non_compliant"
              ? "Non-compliant only"
              : "All statuses";
        const startY = addCountyLetterhead(doc, {
          title: "COMPLIANCE MODULE — DASHBOARD REPORT",
          subtitle: `${this.areaLabel} · ${statusLabel}` +
            (this.filters.departmentName ? ` · Dept: ${this.filters.departmentName}` : "") +
            (this.filters.search ? ` · Search: ${this.filters.search}` : ""),
          periodLabel: `Year ${this.filters.year} · Generated ${new Date().toLocaleString("en-KE")}`,
          logoDataUrl,
          letterhead: this.letterhead
        });

        autoTable(doc, {
          startY,
          head: [["Metric", "Value"]],
          body: [
            ["Total employees (filter)", String(this.stats.totalEmployees || 0)],
            ["List compliant", String(this.stats.listCompliant || 0)],
            ["List non-compliant", String(this.stats.listNonCompliant || 0)],
            ["Skills rate", `${this.stats.skillsRate || 0}%`],
            ["Wealth rate", `${this.stats.wealthRate || 0}%`],
            ["Confirmation due", String(this.stats.confirmationDue || 0)]
          ],
          styles: { fontSize: 8 },
          headStyles: { fillColor: [12, 74, 110], textColor: 255 },
          margin: { left: 14, right: 14 },
          columnStyles: { 0: { cellWidth: 60 }, 1: { cellWidth: 40 } }
        });

        let y = (doc.lastAutoTable && doc.lastAutoTable.finalY) || startY;
        y += 6;

        autoTable(doc, {
          startY: y,
          head: [["Department", "Compliant", "Non-compliant", "Total", "Rate %"]],
          body: this.byDepartment.map((d) => [
            d.department || "",
            String(d.compliant || 0),
            String(d.non_compliant || d.count || 0),
            String(d.total || 0),
            String(this.deptRate(d))
          ]),
          styles: { fontSize: 7.5 },
          headStyles: { fillColor: [12, 74, 110], textColor: 255, fontStyle: "bold" },
          alternateRowStyles: { fillColor: [248, 250, 252] },
          margin: { left: 10, right: 10 }
        });

        y = (doc.lastAutoTable && doc.lastAutoTable.finalY) || y;
        y += 6;

        const isConf = this.filters.area === "confirmation";
        const head = isConf
          ? ["#", "Employee", "Emp No", "ID No", "Department", "Status", "Probation end", "Days"]
          : ["#", "Employee", "Emp No", "ID No", "Department", "Position", "Status"];
        const body = this.employees.map((r, i) => {
          const row = [
            String(i + 1),
            r.employee_name || "",
            r.empNo || "",
            r.nationalId || "",
            r.departmentName || ""
          ];
          if (isConf) {
            row.push(
              r.compliance_status === "compliant" ? "Compliant" : "Non-compliant",
              r.probation_end || "",
              r.remaining_days != null ? String(r.remaining_days) : ""
            );
          } else {
            row.push(
              r.positionTitle || "",
              r.compliance_status === "compliant" ? "Compliant" : "Non-compliant"
            );
          }
          return row;
        });

        autoTable(doc, {
          startY: y,
          head: [head],
          body,
          styles: { fontSize: 6.5, cellPadding: 1.1 },
          headStyles: { fillColor: [12, 74, 110], textColor: 255, fontStyle: "bold", fontSize: 6.5 },
          alternateRowStyles: { fillColor: [248, 250, 252] },
          margin: { left: 10, right: 10 }
        });

        const pageH = doc.internal.pageSize.getHeight();
        const pageW = doc.internal.pageSize.getWidth();
        const pageCount = doc.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i += 1) {
          doc.setPage(i);
          doc.setFontSize(7);
          doc.setTextColor(148, 163, 184);
          const footer =
            (this.letterhead && this.letterhead.reportFooter) ||
            "Official HRMIS document — County Government of Homa Bay — Compliance Module";
          doc.text(footer, 14, pageH - 8);
          doc.text(`Page ${i} of ${pageCount}`, pageW - 14, pageH - 8, { align: "right" });
        }

        doc.save(`compliance_dashboard_${this.filters.area}_${this.filters.year}_${new Date().toISOString().slice(0, 10)}.pdf`);
      } catch (e) {
        console.error(e);
        window.alert(e?.message || "Could not generate PDF.");
      } finally {
        this.exporting = false;
      }
    }
  }
};
</script>

<style scoped>
.cd-wrap { padding: 0; }
.cd-export-group { display: inline-flex; gap: 0.4rem; }
.cd-filters {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(170px, 1fr));
  gap: 0.85rem 1rem;
  align-items: end;
}
.cd-field {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  font-size: 0.85rem;
  color: #475569;
  font-weight: 600;
}
.cd-field-wide { grid-column: span 2; min-width: 220px; }
.cd-charts {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.25rem;
}
.cd-canvas-wrap {
  position: relative;
  height: 280px;
}
.cd-canvas-tall { height: 340px; }
.hr-flex-between { display: flex; justify-content: space-between; align-items: center; }
.hr-ms-2 { margin-left: 0.5rem; }
@media (max-width: 992px) {
  .cd-charts { grid-template-columns: 1fr; }
  .cd-field-wide { grid-column: span 1; }
}
</style>
